import { useEffect, useState } from "react";
import { Paperclip, Send, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const vacio = {
  materia_id: "", docente_id: "", periodo: "", semana: "", titulo: "",
  objetivos: "", contenidos: "", actividades: "", recursos: "",
  archivo_nombre: "", archivo_base64: "",
};

export const PlanificacionForm = ({ materias, docentes, periodos, valor, onGuardar, coordinacion }) => {
  const [form, setForm] = useState({ ...vacio, periodo: periodos[0] || "" });

  useEffect(() => {
    if (valor) setForm({ ...vacio, ...valor, periodo: valor.periodo || periodos[0] || "" });
  }, [valor, periodos]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const subir = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("El archivo no debe superar los 5 MB");
    const reader = new FileReader();
    reader.onload = () => setForm({ ...form, archivo_nombre: file.name, archivo_base64: reader.result });
    reader.readAsDataURL(file);
  };

  const enviar = (enviarRevision) => {
    if (!form.materia_id) return toast.error("Seleccione la materia");
    if (coordinacion && !form.docente_id) return toast.error("Seleccione el docente responsable");
    if (!form.titulo.trim()) return toast.error("Escriba un título para la planificación");
    onGuardar({ ...form, enviar: enviarRevision });
  };

  const materiasVisibles = coordinacion && form.docente_id
    ? materias.filter((m) => m.docente_id === form.docente_id)
    : materias;

  return (
    <div className="grid gap-4" data-testid="form-planificacion">
      {coordinacion && (
        <div className="space-y-2">
          <Label>Docente responsable</Label>
          <select value={form.docente_id} data-testid="plan-docente"
                  onChange={(e) => setForm({ ...form, docente_id: e.target.value, materia_id: "" })}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
            <option value="">Seleccione…</option>
            {docentes.map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}
          </select>
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>Materia</Label>
          <select value={form.materia_id} data-testid="plan-materia" onChange={set("materia_id")}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
            <option value="">Seleccione…</option>
            {materiasVisibles.map((m) => (
              <option key={m.id} value={m.id}>{`${m.nombre} — ${m.grado}`}</option>
            ))}
          </select>
        </div>
        <div className="space-y-2">
          <Label>Periodo</Label>
          <select value={form.periodo} data-testid="plan-periodo" onChange={set("periodo")}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
            {periodos.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>Título de la planificación</Label>
          <Input value={form.titulo} data-testid="plan-titulo" onChange={set("titulo")}
                 placeholder="Unidad 3 · Los seres vivos" />
        </div>
        <div className="space-y-2">
          <Label>Semana o fechas (opcional)</Label>
          <Input value={form.semana} data-testid="plan-semana" onChange={set("semana")}
                 placeholder="Semana del 8 al 12 de junio" />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Objetivos de aprendizaje</Label>
        <Textarea rows={3} value={form.objetivos} data-testid="plan-objetivos" onChange={set("objetivos")} />
      </div>
      <div className="space-y-2">
        <Label>Contenidos</Label>
        <Textarea rows={3} value={form.contenidos} data-testid="plan-contenidos" onChange={set("contenidos")} />
      </div>
      <div className="space-y-2">
        <Label>Actividades y estrategias</Label>
        <Textarea rows={3} value={form.actividades} data-testid="plan-actividades" onChange={set("actividades")} />
      </div>
      <div className="space-y-2">
        <Label>Recursos y evaluación (opcional)</Label>
        <Textarea rows={2} value={form.recursos} data-testid="plan-recursos" onChange={set("recursos")} />
      </div>
      <div className="space-y-2">
        <Label>Archivo adjunto (opcional)</Label>
        <Input type="file" onChange={subir} data-testid="plan-archivo"
               accept=".pdf,.doc,.docx,.xls,.xlsx,image/*" />
        {form.archivo_nombre && (
          <p className="flex items-center gap-1 text-xs text-muted-foreground">
            <Paperclip className="h-3 w-3" /> {form.archivo_nombre}
          </p>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" className="rounded-full" onClick={() => enviar(false)}
                data-testid="guardar-plan-borrador">
          <Save className="mr-2 h-4 w-4" /> Guardar borrador
        </Button>
        <Button className="rounded-full" onClick={() => enviar(true)} data-testid="enviar-plan">
          <Send className="mr-2 h-4 w-4" /> {coordinacion ? "Registrar y notificar" : "Enviar a coordinación"}
        </Button>
      </div>
    </div>
  );
};

export const ESTADOS_PLAN = {
  borrador: { label: "Borrador", variant: "outline" },
  enviada: { label: "En revisión", variant: "default" },
  aprobada: { label: "Aprobada", variant: "secondary" },
  correcciones: { label: "Con observaciones", variant: "destructive" },
  rechazada: { label: "Rechazada", variant: "destructive" },
};
